import React from "react";
import { Card, Button, Tag, Avatar } from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    HistoryOutlined,
    CalendarOutlined,
    TagOutlined,
    BarChartOutlined,
    PercentageOutlined,
    DollarOutlined
} from "@ant-design/icons";
import { cn } from "../../../../utils/cn";
import SOURCES from "../../../../utils/constants";
import { useHistory } from "react-router-dom";

const EnhancedCouponGridView = ({
    record,
    actions = [],
    onCardClick,
}) => {
    const { DISCOUNT_FIXED } = SOURCES;
    const history = useHistory()

    // Filter actions based on conditions
    const filteredActions = actions
        .map((action) => {
            // If action has a condition function, check if it should be shown
            if (action.condition && !action.condition(record)) {
                return null;
            }
            return action;
        })
        .filter(Boolean);

    // Helper to get initials or icon for avatar
    const getAvatarContent = () => {
        return <TagOutlined />;
    };

    const isFixed = record.couponType === 'Fixed';

    return (
        <Card
            hoverable
            className="h-full cursor-auto"
        // onClick={() => onCardClick(record)}
        >
            <div className="space-y-4">
                {/* Coupon Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                        <Avatar
                            size={48}
                            className="flex-shrink-0 font-semibold text-lg !bg-primary"
                            icon={getAvatarContent()}
                        />
                        <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-base mb-1 truncate text-primary">
                                {record.code || "N/A"}
                            </h3>
                            <Tag color="cyan">{record.couponType}</Tag>
                        </div>
                    </div>
                    {/* Show History Button in Header */}
                    <Button
                        type="link"
                        className="!text-primary hover:!text-primary/80 !p-0 !h-auto flex items-center gap-1"
                        onClick={(e) => {
                            e.stopPropagation();
                            history.push(`/admin/coupons/usage-history?id=${record.id}`)
                        }}
                    >
                        <HistoryOutlined />
                        <span className="text-xs">History</span>
                    </Button>
                </div>

                {/* Coupon Info */}
                <div className="space-y-2 border-b border-gray-200 pb-3">
                    {/* Amount */}
                    <div className="flex items-center text-sm text-gray-600">
                        {isFixed ? <DollarOutlined className="mr-2 flex-shrink-0" /> : <PercentageOutlined className="mr-2 flex-shrink-0" />}
                        <span className="truncate font-semibold">
                            Amount: <span className="text-gray-800">{record.amount}</span>
                        </span>
                    </div>

                    {/* Expiry Date */}
                    <div className="flex items-center text-sm text-gray-600">
                        <CalendarOutlined className="mr-2 flex-shrink-0" />
                        <span className="truncate">Expires: {record.expiryDate || 'N/A'}</span>
                    </div>
                </div>

                {/* Usage Stats - Matching badging style */}
                <div className="space-y-3">

                    {/* Usage */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 font-medium">
                            Usage:
                        </span>
                        <Tag color="cyan">
                            {record.usage}
                        </Tag>
                    </div>

                    {/* Total Usage Allowed */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 font-medium">
                            Allowed:
                        </span>
                        <Tag color={record.usageAllowed === 'No Limit' ? 'green' : 'volcano'}>
                            {record.usageAllowed}
                        </Tag>
                    </div>
                </div>

                {/* Action Buttons - Only Edit/Delete */}
                {filteredActions.length > 0 && (
                    <div
                        className={cn(
                            "grid gap-2 pt-3 border-t border-gray-200",
                            filteredActions.length === 1 ? "grid-cols-1" : "grid-cols-2"
                        )}
                    >
                        {filteredActions.map((action, idx) => (
                            <Button
                                key={idx}
                                title={action.title ?? action.label}
                                className={cn(
                                    "text-sm font-medium h-9 my-1 !border-primary !text-primary hover:!bg-primary hover:!text-white !min-w-full flex justify-center items-center transition-colors duration-150 ease-in-out",
                                    action.className
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(record);
                                }}
                            >
                                {action.icon &&
                                    React.cloneElement(action.icon, { className: "mr-2" })}
                                {action.label}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default EnhancedCouponGridView;